import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import LandingPage     from './pages/LandingPage';
import AppShell from './components/AppShell';
import InformationPage from './pages/InformationPage';
import ActivityPage from './pages/ActivityPage';
import ReportPage from './pages/ReportPage';
import LoginPage       from './pages/LoginPage';
import HomePage        from './pages/HomePage';
import ChatPage        from './pages/ChatPage';
import WalletPage      from './pages/WalletPage';
import AdminPage       from './pages/AdminPage';
import ProfilePage     from './pages/ProfilePage';
import TransactionPage from './pages/TransactionPage';
import MyCouponsPage   from './pages/MyCouponsPage';
import { perfMark } from './utils/perf';
import { fetchJson } from './utils/fetchJson';
import Icon from "./components/Icon";

// ── แผนผัง English → ภาษาไทย (จาก ipwho.is) ──────────────────────────────
const EN_TO_TH = {
  "Bangkok":"กรุงเทพมหานคร","Krung Thep Maha Nakhon":"กรุงเทพมหานคร",
  "Chiang Mai":"เชียงใหม่","Chiang Rai":"เชียงราย",
  "Nonthaburi":"นนทบุรี","Pathum Thani":"ปทุมธานี","Samut Prakan":"สมุทรปราการ",
  "Samut Sakhon":"สมุทรสาคร","Samut Songkhram":"สมุทรสงคราม",
  "Nakhon Ratchasima":"นครราชสีมา","Nakhon Si Thammarat":"นครศรีธรรมราช",
  "Nakhon Sawan":"นครสวรรค์","Nakhon Pathom":"นครปฐม","Nakhon Phanom":"นครพนม",
  "Nakhon Nayok":"นครนายก","Khon Kaen":"ขอนแก่น","Udon Thani":"อุดรธานี",
  "Ubon Ratchathani":"อุบลราชธานี","Phuket":"ภูเก็ต","Surat Thani":"สุราษฎร์ธานี",
  "Songkhla":"สงขลา","Chon Buri":"ชลบุรี","Chonburi":"ชลบุรี","Rayong":"ระยอง",
  "Phitsanulok":"พิษณุโลก","Lampang":"ลำปาง","Lamphun":"ลำพูน",
  "Phrae":"แพร่","Nan":"น่าน","Phayao":"พะเยา","Mae Hong Son":"แม่ฮ่องสอน",
  "Tak":"ตาก","Sukhothai":"สุโขทัย","Uttaradit":"อุตรดิตถ์",
  "Phichit":"พิจิตร","Phetchabun":"เพชรบูรณ์","Kamphaeng Phet":"กำแพงเพชร",
  "Ang Thong":"อ่างทอง","Sing Buri":"สิงห์บุรี","Lop Buri":"ลพบุรี",
  "Saraburi":"สระบุรี","Chai Nat":"ชัยนาท",
  "Ayutthaya":"พระนครศรีอยุธยา","Phra Nakhon Si Ayutthaya":"พระนครศรีอยุธยา",
  "Suphan Buri":"สุพรรณบุรี","Kanchanaburi":"กาญจนบุรี",
  "Ratchaburi":"ราชบุรี","Phetchaburi":"เพชรบุรี",
  "Prachuap Khiri Khan":"ประจวบคีรีขันธ์","Chumphon":"ชุมพร",
  "Ranong":"ระนอง","Phang Nga":"พังงา","Krabi":"กระบี่","Trang":"ตรัง",
  "Satun":"สตูล","Pattani":"ปัตตานี","Yala":"ยะลา","Narathiwat":"นราธิวาส",
  "Sakon Nakhon":"สกลนคร","Mukdahan":"มุกดาหาร","Kalasin":"กาฬสินธุ์",
  "Roi Et":"ร้อยเอ็ด","Maha Sarakham":"มหาสารคาม","Buriram":"บุรีรัมย์",
  "Surin":"สุรินทร์","Si Sa Ket":"ศรีสะเกษ","Amnat Charoen":"อำนาจเจริญ",
  "Yasothon":"ยโสธร","Loei":"เลย","Nong Khai":"หนองคาย",
  "Nong Bua Lam Phu":"หนองบัวลำภู","Bueng Kan":"บึงกาฬ",
  "Prachin Buri":"ปราจีนบุรี","Sa Kaeo":"สระแก้ว","Chanthaburi":"จันทบุรี",
  "Trat":"ตราด","Chachoengsao":"ฉะเชิงเทรา","Phatthalung":"พัทลุง",
  "Chaiyaphum":"ชัยภูมิ",
};

function toThaiProvince(region) {
  if (!region) return null;
  return EN_TO_TH[region] || region;
}

async function fetchRegion() {
  // ลอง API หลักก่อน
  const doneMain = perfMark("App: fetch ipwho.is (province)");
  try {
    const d = await fetchJson("https://ipwho.is/");
    doneMain(d.success ? "ok" : "no region");
    if (d.success && d.region) return d.region;
  } catch (err) { doneMain("failed: " + err.message); }
  // fallback → ipapi.co
  const doneFallback = perfMark("App: fetch ipapi.co (province fallback)");
  try {
    const d = await fetchJson("https://ipapi.co/json/");
    doneFallback(d.region ? "ok" : "no region");
    if (d.region) return d.region;
  } catch (err) { doneFallback("failed: " + err.message); }
  return null;
}

const PROVINCE_RECHECK_MS = 6 * 60 * 60 * 1000; // เช็กจังหวัดซ้ำห่างกันอย่างน้อย 6 ชม. — ไม่ต้องยิง API ต่างประเทศทุกครั้งที่เปิดแอป

async function ensureProvince(uid) {
  const done = perfMark("App: ensureProvince");
  try {
    const snap = await getDoc(doc(db, "users", uid));
    if (!snap.exists()) { done("user doc not found"); return; }

    const data = snap.data();
    const lastSeenAt = data.lastSeenAt?.toDate ? data.lastSeenAt.toDate() : (data.lastSeenAt ? new Date(data.lastSeenAt) : null);
    if (lastSeenAt && Date.now() - lastSeenAt.getTime() < PROVINCE_RECHECK_MS) {
      done("ข้าม — เช็กไปแล้วเมื่อไม่นานนี้");
      return;
    }

    const region = await fetchRegion();
    if (!region) { done("ไม่ได้ region"); return; }

    const detected = toThaiProvince(region);
    const hasManual = !!data.manualProvince;
    await updateDoc(doc(db, "users", uid), {
      // เคารพจังหวัดที่ผู้ใช้ตั้งเองใน ProfilePage — อัปเดต province อัตโนมัติ
      // เฉพาะกรณีที่ผู้ใช้ยังไม่เคยตั้งเอง
      ...(hasManual ? {} : { province: detected }),
      lastSeenProvince: detected,            // บันทึกแยกสำหรับ Admin ดู
      lastSeenAt: new Date(),
    });
    done("เสร็จ: " + detected);
  } catch (err) { done("error: " + err.message); }
}

// ── Admin Protected Route ─────────────────────────────────────────────────
function ProtectedRoute() {
  const location = useLocation();
  const [ready, setReady] = useState(false);
  const [sessionUid, setSessionUid] = useState(null);
  useEffect(() => onAuthStateChanged(auth, (current) => {
    if (current) {
      let cached = {};
      try { cached = JSON.parse(localStorage.getItem("klang-user") || "{}"); } catch (_) {}
      localStorage.setItem("klang-user", JSON.stringify({
        ...(cached?.uid === current.uid ? cached : {}),
        uid: current.uid, email: current.email,
        name: cached?.uid === current.uid ? cached.name : current.displayName,
      }));
    } else localStorage.removeItem("klang-user");
    setSessionUid(current?.uid || null);
    setReady(true);
  }), []);
  if (!ready) return <div role="status">กำลังตรวจสอบการเข้าสู่ระบบ...</div>;
  return sessionUid ? <Outlet key={sessionUid} /> : <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
}

function AdminRoute() {
  const [status, setStatus] = useState("loading");
  const user = auth.currentUser;

  useEffect(() => {
    if (!user?.uid) { setStatus("denied"); return; }
    getDoc(doc(db, "users", user.uid)).then((snap) => {
      if (snap.exists() && snap.data().isAdmin === true) setStatus("allowed");
      else setStatus("denied");
    }).catch(() => setStatus("denied"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === "loading") return (
    <div style={{ minHeight:"100vh", background:"#0a0f0d", display:"flex", alignItems:"center", justifyContent:"center", color:"#7a9484", fontFamily:"Prompt,sans-serif" }}>
      กำลังตรวจสอบสิทธิ์...
    </div>
  );
  if (status === "denied") return (
    <div style={{ minHeight:"100vh", background:"#0a0f0d", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", fontFamily:"Prompt,sans-serif" }}>
      <div style={{ marginBottom:16, color:"#ff5555" }}><Icon name="ban" size={48} /></div>
      <div style={{ color:"#ff5555", fontSize:"1.1rem", fontWeight:700, marginBottom:8 }}>ไม่มีสิทธิ์เข้าถึง</div>
      <div style={{ color:"#7a9484", fontSize:"0.85rem", marginBottom:24 }}>หน้านี้สำหรับ Admin เท่านั้น</div>
      <button onClick={() => window.location.href = "/home"}
        style={{ padding:"10px 24px", background:"#00e87a", border:"none", borderRadius:10, cursor:"pointer", fontFamily:"Prompt,sans-serif", fontWeight:600, fontSize:"0.9rem" }}>
        กลับหน้าหลัก
      </button>
    </div>
  );
  return <AdminPage />;
}

export default function App() {
  useEffect(() => onAuthStateChanged(auth, (user) => {
    if (user) ensureProvince(user.uid);
  }), []);

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/"             element={<LandingPage />} />
        <Route path="/login"        element={<LoginPage />} />
        <Route element={<AppShell />}>
          <Route path="/fees" element={<InformationPage type="fees" />} />
          <Route path="/help" element={<InformationPage type="help" />} />
          <Route path="/how-it-works" element={<InformationPage type="how" />} />
        </Route>
        <Route element={<ProtectedRoute />}>
        <Route element={<AppShell />}>
        <Route path="/home"         element={<HomePage />} />
        <Route path="/rooms" element={<HomePage view="rooms" />} />
        <Route path="/wallet"       element={<WalletPage />} />
        <Route path="/profile"      element={<ProfilePage />} />
        <Route path="/transactions" element={<TransactionPage />} />
        <Route path="/my-coupons"   element={<MyCouponsPage />} />
        <Route path="/reviews" element={<ActivityPage type="reviews" />} />
        <Route path="/notifications" element={<ActivityPage />} />
        <Route path="/report" element={<ReportPage />} />
        </Route>
        <Route path="/chat/:roomId" element={<ChatPage />} />
        <Route path="/admin"        element={<AdminRoute />} />
        </Route>
        <Route path="*"             element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
